import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem, Autocomplete,
  Tooltip, alpha, Avatar, Divider, Skeleton, ToggleButton, ToggleButtonGroup,
  Checkbox, FormControlLabel, CircularProgress
} from '@mui/material';
import {
  Add, Edit, Delete, Search, MonetizationOn, Close, CalendarToday, Visibility, Receipt
} from '@mui/icons-material';
import { collectionsAPI, providersAPI, contractorsAPI, providerServicesAPI, providerInvoicesAPI, contractorInvoicesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { fullName } from '../../utils/formatters';
import { useNavigate } from 'react-router-dom';
import { useTableSort } from '../../components/useTableSort';

const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

const emptyRow = { service: '', serviceName: '', collectionAmount: 0, billingRate: 0, billingAmount: 0, commissionRate: 0, commission: 0, profit: 0, isFixed: false };

const Collections = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyTimezone = user?.company?.settings?.timezone || 'America/New_York';
  const cp = user?.permissions?.collections || {};
  const isSuperAdmin = user?.isSuperAdmin || user?.role === 'super_admin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';
  // Permissions – admins/super admins see everything
  const canViewBilling = isAdmin || cp.canViewBillingRate !== false;
  const canViewInvoice = isAdmin || cp.canViewInvoiceAmount !== false;
  const canViewCommission = isAdmin || cp.canViewCommission !== false;
  const canViewProfit = isAdmin || cp.canViewProfit !== false;
  const canViewProvider = isAdmin || cp.canViewProviderSection !== false;
  const canViewContractor = isAdmin || cp.canViewContractorSection !== false;
  const canAdd = isAdmin || cp.canAdd !== false;
  const canEdit = isAdmin || cp.canEdit !== false;
  const canDelete = isAdmin || cp.canDelete !== false;

  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [providerOptionsLoading, setProviderOptionsLoading] = useState(false);
  const [providerDetailsLoading, setProviderDetailsLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(20);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [form, setForm] = useState({ provider: null, contractor: '', collectionPeriod: '', services: [{ ...emptyRow }] });
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const uniqById = useCallback((list) => {
    const seen = new Set();
    const out = [];
    for (const item of (Array.isArray(list) ? list : [])) {
      const id = item?._id ?? item?.id;
      if (!id) continue;
      if (seen.has(String(id))) continue;
      seen.add(String(id));
      out.push(item);
    }
    return out;
  }, []);

  const sortProvidersByName = useCallback((list) => {
    return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
      const an = (fullName(a) || '').toLowerCase();
      const bn = (fullName(b) || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, []);

  const loadProviderOptions = useCallback(async (includeInactive) => {
    setProviderOptionsLoading(true);
    try {
      const params = { limit: 10000, includeDetails: false };
      if (!includeInactive) params.isActive = true;
      const p = await providersAPI.getAll(params);
      const list = uniqById(Array.isArray(p.data) ? p.data : p.data?.data || []);
      setProviders(sortProvidersByName(list));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load providers');
    } finally {
      setProviderOptionsLoading(false);
    }
  }, [sortProvidersByName, uniqById]);

  const load = useCallback(async () => {
    try {
      const [c, ct, sv] = await Promise.all([
        collectionsAPI.getAll({ limit: 10000 }),
        contractorsAPI.getAll({ limit: 10000 }),
        providerServicesAPI.getAll({ limit: 10000 })
      ]);
      setRows(Array.isArray(c.data) ? c.data : c.data?.data || []);
      setContractors(Array.isArray(ct.data) ? ct.data : ct.data?.data || []);
      setAllServices(Array.isArray(sv.data) ? sv.data : sv.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Collections | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProviderOptions(includeInactiveProviders); }, [includeInactiveProviders, loadProviderOptions]);

  const providerOptions = useMemo(() => {
    const base = includeInactiveProviders ? providers : providers.filter(p => p.isActive !== false);
    if (includeInactiveProviders) return base;
    const selected = form.provider;
    if (selected && selected.isActive === false) {
      const exists = base.some(p => String(p._id ?? p.id) === String(selected._id ?? selected.id));
      return exists ? base : [selected, ...base];
    }
    return base;
  }, [providers, includeInactiveProviders, form.provider]);

  // Available services: if provider is selected, show that provider's services; else show all
  const availableServices = form.provider?.services?.length > 0
    ? form.provider.services.map(s => ({ _id: s.service?._id || s.service || '', name: s.service?.name || '', billingType: s.service?.billingType || '%' }))
    : allServices.map(s => ({ _id: s._id, name: s.name, billingType: s.billingType || '%' }));

  // --- Column definitions for sort/filter ---
  const columns = useMemo(() => [
    { id: 'collectionId', label: 'ID', getValue: r => r.collectionId || '' },
    { id: 'collectionPeriod', label: 'Period', getValue: r => r.collectionPeriod || '', getDisplay: r => r.collectionPeriod ? new Date(r.collectionPeriod).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '—' },
    ...(canViewProvider ? [{ id: 'provider', label: 'Provider', getValue: r => fullName(r.provider) || '' }] : []),
    ...(canViewContractor ? [{ id: 'contractor', label: 'Contractor', getValue: r => fullName(r.contractor) || '' }] : []),
    { id: 'totalCollectionAmount', label: 'Collected', getValue: r => parseFloat(r.totalCollectionAmount) || 0, align: 'right' },
    ...(canViewInvoice ? [{ id: 'totalBillingAmount', label: 'Billed', getValue: r => parseFloat(r.totalBillingAmount) || 0, align: 'right' }] : []),
    ...(canViewProfit ? [{ id: 'totalProfit', label: 'Profit', getValue: r => parseFloat(r.totalProfit) || 0, align: 'right' }] : []),
    { id: 'providerInv', label: 'P. Invoice', getValue: r => r.providerInvoice ? 'Has Invoice' : 'No Invoice' },
    { id: 'contractorInv', label: 'C. Invoice', getValue: r => (r.contractorInvoices && r.contractorInvoices.length > 0) ? 'Has Invoice' : 'No Invoice' },
    { id: 'invoiceGenerated', label: 'Invoice Generated', getValue: r => {
      const inv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices[0]);
      if (!inv || !inv.createdAt) return '';
      return new Date(inv.createdAt).toLocaleDateString('en-US', { timeZone: companyTimezone });
    }, getDisplay: r => {
      const inv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices[0]);
      if (!inv || !inv.createdAt) return '—';
      const date = new Date(inv.createdAt).toLocaleDateString('en-US', { timeZone: companyTimezone });
      const user = inv.createdBy ? `${inv.createdBy.firstName || ''} ${inv.createdBy.lastName || ''}`.trim() : '';
      return user ? `${date} by ${user}` : date;
    }},
    { id: 'payment', label: 'Payment', getValue: r => {
      const hasInv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices.length > 0);
      if (!hasInv) return 'No Invoice';
      const inv = r.providerInvoice || r.contractorInvoices?.[0];
      const paid = parseFloat(inv?.amountPaid) || 0; const total = parseFloat(inv?.total) || 0;
      if (paid >= total && total > 0) return 'Paid'; if (paid > 0) return 'Partial'; return 'Unpaid';
    }},
  ], [canViewProvider, canViewContractor, canViewInvoice, canViewProfit, companyTimezone]);

  // Text-search + status filtered rows
  const searchFiltered = useMemo(() => {
    let data = rows;
    // Status filter
    if (statusFilter !== 'all') {
      data = data.filter(r => {
        const hasInv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices.length > 0);
        if (statusFilter === 'uninvoiced') return !hasInv;
        if (statusFilter === 'invoiced') return hasInv;
        if (!hasInv) return false;
        const inv = r.providerInvoice || r.contractorInvoices?.[0];
        const paid = parseFloat(inv?.amountPaid) || 0;
        const total = parseFloat(inv?.total) || 0;
        if (statusFilter === 'unpaid') return paid === 0;
        if (statusFilter === 'partial') return paid > 0 && paid < total;
        if (statusFilter === 'paid') return paid >= total && total > 0;
        return true;
      });
    }
    if (!search) return data;
    return data.filter(r => {
      const pn = fullName(r.provider) || ''; const cn = fullName(r.contractor) || ''; const id = r.collectionId || '';
      return [pn, cn, id].some(v => v.toLowerCase().includes(search.toLowerCase()));
    });
  }, [rows, search, statusFilter]);

  const { sortedData: filtered, renderHeader, renderFilterPopover, renderClearFilters, renderColumnToggle, isColumnVisible, visibleColumns } = useTableSort(columns, searchFiltered, { defaultSort: 'collectionPeriod', defaultDir: 'desc', storageKey: 'collections' });

  const recalcRow = (row) => {
    const ca = parseFloat(row.collectionAmount) || 0;
    const br = parseFloat(row.billingRate) || 0;
    const cr = parseFloat(row.commissionRate) || 0;
    const ba = row.isFixed ? br : parseFloat((ca * br / 100).toFixed(2));
    const com = parseFloat((ca * cr / 100).toFixed(2));
    const profit = parseFloat((ba - com).toFixed(2));
    return { ...row, billingAmount: ba, commission: com, profit };
  };

  const applySelectedProvider = (provDetailed) => {
    const svcRows = (provDetailed.services || []).filter(s => s.isActive !== false).map(s => ({
      service: s.service?._id || s.service || '',
      serviceName: s.service?.name || '',
      collectionAmount: 0, billingRate: s.billingRate || provDetailed.billingRate || 0,
      billingAmount: 0, commissionRate: 0, commission: 0, profit: 0,
      isFixed: s.service?.billingType === '$'
    }));

    setForm(f => {
      // Auto-select first active assigned contractor and apply their commission
      const activeAssignments = (provDetailed.assignedContractors || []).filter(a => a.isActive !== false);
      let autoContractorId = f.contractor || '';
      if (!autoContractorId && activeAssignments.length > 0) {
        const first = activeAssignments[0];
        autoContractorId = String(first.contractor?.id || first.contractor?._id || first.contractorId || '');
      }
      if (autoContractorId && activeAssignments.length > 0) {
        const assignment = activeAssignments.find(a => {
          const cId = a.contractor?.id || a.contractor?._id || a.contractorId;
          return String(cId) === String(autoContractorId);
        });
        if (assignment) {
          svcRows.forEach(row => { row.commissionRate = assignment.payRate || 0; });
          svcRows.forEach((row, idx) => { svcRows[idx] = recalcRow(row); });
        }
      }
      return { ...f, provider: provDetailed, contractor: autoContractorId, services: svcRows.length > 0 ? svcRows : [{ ...emptyRow }] };
    });
  };

  const handleProviderChange = async (prov) => {
    if (!prov) {
      setForm(f => ({ ...f, provider: null, contractor: '', services: [{ ...emptyRow }] }));
      return;
    }
    // When editing, skip if the provider hasn't actually changed
    if (editing) {
      const currentProvId = String(form.provider?._id || form.provider?.id || '');
      const newProvId = String(prov._id || prov.id || '');
      if (currentProvId === newProvId) return;
    }
    setProviderDetailsLoading(true);
    try {
      const resp = await providersAPI.getById(prov._id || prov.id);
      const detailed = resp.data;
      applySelectedProvider(detailed);
      setProviders(prev => sortProvidersByName(uniqById([...(prev || []), detailed])));
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to load provider details');
    } finally {
      setProviderDetailsLoading(false);
    }
  };

  // When editing an existing collection, ensure we have provider details for contractor chips, etc.
  useEffect(() => {
    const providerId = form.provider?._id || form.provider?.id;
    if (!open || !providerId) return;
    if (Array.isArray(form.provider?.services) || Array.isArray(form.provider?.assignedContractors)) return;
    let alive = true;
    (async () => {
      try {
        setProviderDetailsLoading(true);
        const resp = await providersAPI.getById(providerId);
        const detailed = resp.data;
        if (!alive) return;
        setForm(f => ({ ...f, provider: detailed }));
        setProviders(prev => sortProvidersByName(uniqById([...(prev || []), detailed])));
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setProviderDetailsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, form.provider, sortProvidersByName, uniqById]);

  const handleContractorChange = (contractorId) => {
    setForm(f => {
      const prov = f.provider;
      let svcs = [...f.services];
      if (prov && contractorId && prov.assignedContractors) {
        const assignment = prov.assignedContractors.find(a => {
          const cId = a.contractor?.id || a.contractor?._id || a.contractorId;
          return String(cId) === String(contractorId) && a.isActive !== false;
        });
        if (assignment) {
          svcs = svcs.map(row => recalcRow({ ...row, commissionRate: assignment.payRate || 0 }));
        }
      }
      return { ...f, contractor: contractorId, services: svcs };
    });
  };

  const handleServiceSelect = (idx, serviceId) => {
    const svc = availableServices.find(s => s._id === serviceId) || allServices.find(s => s._id === serviceId);
    setForm(f => {
      const svcs = [...f.services];
      svcs[idx] = recalcRow({
        ...svcs[idx],
        service: serviceId,
        serviceName: svc?.name || '',
        isFixed: svc?.billingType === '$',
        billingRate: svc?.billingType === '$' ? (svcs[idx].billingRate || 0) : (f.provider?.billingRate || svcs[idx].billingRate || 0)
      });
      return { ...f, services: svcs };
    });
  };

  const updateServiceRow = (idx, field, val) => {
    setForm(f => {
      const svcs = [...f.services];
      svcs[idx] = { ...svcs[idx], [field]: val };
      svcs[idx] = recalcRow(svcs[idx]);
      return { ...f, services: svcs };
    });
  };

  const addServiceRow = () => setForm(f => ({ ...f, services: [...f.services, { ...emptyRow }] }));
  const removeServiceRow = (idx) => setForm(f => ({ ...f, services: f.services.filter((_, i) => i !== idx) }));

  const totals = (svcs) => {
    const s = svcs || [];
    return {
      collected: s.reduce((a, r) => a + (parseFloat(r.collectionAmount) || 0), 0),
      billed: s.reduce((a, r) => a + (parseFloat(r.billingAmount) || 0), 0),
      commission: s.reduce((a, r) => a + (parseFloat(r.commission) || 0), 0),
      profit: s.reduce((a, r) => a + (parseFloat(r.profit) || 0), 0)
    };
  };

  const handleOpen = (row) => {
    if (row) {
      setEditing(row._id);
      // Map services from the collection data, stripping Mongoose _id to avoid update issues
      const mappedServices = (row.services || []).map(s => ({
        service: s.service?._id || s.service || '',
        serviceName: s.service?.name || s.serviceName || '',
        collectionAmount: s.collectionAmount || 0,
        billingRate: s.billingRate || 0, billingAmount: s.billingAmount || 0,
        commissionRate: s.commissionRate || 0, commission: s.commission || 0,
        profit: s.profit || 0, isFixed: s.isFixed || false
      }));
      setForm({
        provider: (() => {
          const rowProviderObj = (row.provider && typeof row.provider === 'object') ? row.provider : null;
          const rowProviderId = rowProviderObj?._id || rowProviderObj?.id || row.provider;
          return providers.find(p => String(p._id ?? p.id) === String(rowProviderId)) || rowProviderObj || (rowProviderId ? { _id: rowProviderId, id: rowProviderId } : null);
        })(),
        contractor: row.contractor?._id || row.contractor || '',
        collectionPeriod: row.collectionPeriod ? row.collectionPeriod.slice(0, 7) : '',
        services: mappedServices.length > 0 ? mappedServices : [{ ...emptyRow }]
      });
    } else {
      setEditing(null);
      // Use company timezone to determine current month/year for new collections
      const nowInTz = new Date(new Date().toLocaleString('en-US', { timeZone: companyTimezone }));
      const defaultPeriod = `${nowInTz.getFullYear()}-${String(nowInTz.getMonth() + 1).padStart(2, '0')}`;
      setForm({ provider: null, contractor: '', collectionPeriod: defaultPeriod, services: [{ ...emptyRow }] });
    }
    setOpen(true);
  };

  const [saveAndGenerateLoading, setSaveAndGenerateLoading] = useState(false);

  const handleSave = async (generateInvoice = false) => {
    try {
      if (generateInvoice) setSaveAndGenerateLoading(true);
      const t = totals(form.services);
      const payload = {
        provider: form.provider?._id, contractor: form.contractor || undefined,
        collectionPeriod: form.collectionPeriod + '-01', services: form.services,
        totalCollectionAmount: t.collected, totalBillingAmount: t.billed,
        totalCommission: t.commission, totalProfit: t.profit
      };
      let collectionId;
      if (editing) {
        await collectionsAPI.update(editing, payload);
        collectionId = editing;
      } else {
        const res = await collectionsAPI.create(payload);
        collectionId = res.data?._id || res.data?.id;
      }

      if (generateInvoice && collectionId) {
        try {
          const invoiceRes = await collectionsAPI.generateInvoices(collectionId);
          const msg = invoiceRes.data?.message || 'Invoices generated';
          toast.success(msg);
        } catch (invErr) {
          toast.warn(invErr.response?.data?.message || 'Collection saved but invoice generation failed');
        }
      } else {
        toast.success(editing ? 'Collection updated' : 'Collection created');
      }

      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaveAndGenerateLoading(false); }
  };

  const handleDelete = async () => {
    try {
      const id = deleteId?.id || deleteId;
      const force = deleteId?.force;
      await collectionsAPI.delete(id, force ? { force: 'true' } : undefined);
      setDeleteId(null); load(); toast.success('Collection deleted');
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  };

  // Count visible columns for empty-state colSpan
  const listColCount = visibleColumns.length + 1; // visible columns + Actions

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Collections</Typography>
          <Typography variant="body2" color="text.secondary">Track provider collections and billing</Typography>
        </Box>
        {canAdd && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
            sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 4px 14px rgba(245,158,11,0.3)', px: 3, fontWeight: 700 }}>
            New Collection
          </Button>
        )}
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search collections..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 320 }} />
            <ToggleButtonGroup size="small" value={statusFilter} exclusive
              onChange={(e, v) => { if (v !== null) { setStatusFilter(v); setPage(0); } }}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 600 } }}>
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="uninvoiced">Uninvoiced</ToggleButton>
              <ToggleButton value="unpaid">Unpaid</ToggleButton>
              <ToggleButton value="partial">Partial</ToggleButton>
              <ToggleButton value="paid">Paid</ToggleButton>
            </ToggleButtonGroup>
            {renderColumnToggle()}
            {renderClearFilters()}
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  {renderHeader(<TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>Actions</TableCell>)}
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={listColCount} sx={{ py: 6, textAlign: 'center' }}>
                          <MonetizationOn sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No collections found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => (
                        <TableRow key={r._id} hover sx={{ '&:hover': { bgcolor: alpha('#f59e0b', 0.03) } }}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#d97706', fontFamily: 'monospace' }}>{r.collectionId}</Typography>
                          </TableCell>
                          {isColumnVisible('collectionPeriod') && <TableCell>
                            <Typography sx={{ fontSize: '0.82rem' }}>{r.collectionPeriod ? new Date(r.collectionPeriod).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '—'}</Typography>
                          </TableCell>}
                          {canViewProvider && isColumnVisible('provider') && (
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{fullName(r.provider) || 'N/A'}</Typography>
                            </TableCell>
                          )}
                          {canViewContractor && isColumnVisible('contractor') && (
                            <TableCell>
                              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{fullName(r.contractor) || '—'}</Typography>
                            </TableCell>
                          )}
                          {isColumnVisible('totalCollectionAmount') && <TableCell align="right">
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#3b82f6' }}>{fmt(r.totalCollectionAmount)}</Typography>
                          </TableCell>}
                          {canViewInvoice && isColumnVisible('totalBillingAmount') && (
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#d97706' }}>{fmt(r.totalBillingAmount)}</Typography>
                            </TableCell>
                          )}
                          {canViewProfit && isColumnVisible('totalProfit') && (
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: r.totalProfit >= 0 ? '#059669' : '#dc2626' }}>{fmt(r.totalProfit)}</Typography>
                            </TableCell>
                          )}
                          {isColumnVisible('providerInv') && <TableCell>
                            {r.providerInvoice ? (
                              <Chip icon={<Receipt sx={{ fontSize: 12 }} />} label={`P: ${r.providerInvoice.invoiceNumber || r.providerInvoice.invoiceId || 'Yes'}`}
                                size="small" onClick={async () => { try { const res = await providerInvoicesAPI.downloadPDF(r.providerInvoice._id); window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank'); } catch { toast.error('Failed to open invoice PDF'); } }}
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#3b82f6', 0.1), color: '#2563eb', cursor: 'pointer', '& .MuiChip-icon': { color: 'inherit' } }} />
                            ) : (
                              <Chip label="P: —" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#94a3b8' }} />
                            )}
                          </TableCell>}
                          {isColumnVisible('contractorInv') && <TableCell>
                            {r.contractorInvoices && r.contractorInvoices.length > 0 ? (
                              <Chip icon={<Receipt sx={{ fontSize: 12 }} />} label={`C: ${r.contractorInvoices[0].invoiceNumber || r.contractorInvoices[0].invoiceId || 'Yes'}`}
                                size="small" onClick={async () => { try { const res = await contractorInvoicesAPI.downloadPDF(r.contractorInvoices[0]._id); window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank'); } catch { toast.error('Failed to open invoice PDF'); } }}
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#8b5cf6', 0.1), color: '#7c3aed', cursor: 'pointer', '& .MuiChip-icon': { color: 'inherit' } }} />
                            ) : (
                              <Chip label="C: —" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#94a3b8' }} />
                            )}
                          </TableCell>}
                          {isColumnVisible('invoiceGenerated') && <TableCell>
                            {(() => {
                              const inv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices[0]);
                              if (!inv || !inv.createdAt) return <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>—</Typography>;
                              const user = inv.createdBy ? `${inv.createdBy.firstName || ''} ${inv.createdBy.lastName || ''}`.trim() : '';
                              return (
                                <Box>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{new Date(inv.createdAt).toLocaleDateString('en-US', { timeZone: companyTimezone })}</Typography>
                                  {user && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>by {user}</Typography>}
                                </Box>
                              );
                            })()}
                          </TableCell>}
                          {isColumnVisible('payment') && <TableCell>
                            {(() => {
                              const hasInv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices.length > 0);
                              const inv = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices[0]);
                              if (!hasInv) return <Chip label="No Invoice" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#94a3b8' }} />;
                              const paid = parseFloat(inv?.amountPaid) || 0;
                              const total = parseFloat(inv?.total) || 0;
                              if (paid >= total && total > 0) return <Chip label="Paid" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.1), color: '#10b981' }} />;
                              if (paid > 0) return <Chip label="Partial" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#f97316', 0.1), color: '#f97316' }} />;
                              return <Chip label="Unpaid" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#ef4444', 0.1), color: '#ef4444' }} />;
                            })()}
                          </TableCell>}
                          <TableCell align="right">
                            <Tooltip title="View"><IconButton size="small" onClick={() => { setViewData(r); setViewOpen(true); }} sx={{ color: '#3b82f6' }}><Visibility sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(r)} sx={{ color: '#f59e0b' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
                            {canDelete && (() => {
                              const hasInvoices = r.providerInvoice || (r.contractorInvoices && r.contractorInvoices.length > 0);
                              return hasInvoices
                                ? <Tooltip title="Force delete — will unlink invoices"><IconButton size="small" onClick={() => setDeleteId({ id: r._id || r.id, force: true })} sx={{ color: '#f97316' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                : <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId({ id: r._id || r.id, force: false })} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>;
                            })()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Filter Popover */}
      {renderFilterPopover()}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: 2 }}>
              <MonetizationOn sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{editing ? 'Edit Collection' : 'New Collection'}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Enter collection details and services</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8' }}>Provider *</Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={includeInactiveProviders} onChange={(e) => setIncludeInactiveProviders(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Include inactive</Typography>}
                />
              </Box>
              <Autocomplete
                options={providerOptions} getOptionLabel={o => fullName(o) || ''}
                loading={providerOptionsLoading || providerDetailsLoading}
                value={form.provider} onChange={(_, v) => handleProviderChange(v)}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select provider..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2, bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: '#e2e8f0', borderWidth: 1.5 },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 2 },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(providerOptionsLoading || providerDetailsLoading) ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Collection Period *</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField select size="small" value={form.collectionPeriod ? parseInt(form.collectionPeriod.split('-')[1]) || '' : ''}
                  onChange={e => {
                    const m = String(e.target.value).padStart(2, '0');
                    const y = form.collectionPeriod ? form.collectionPeriod.split('-')[0] : new Date().getFullYear().toString();
                    setForm(f => ({ ...f, collectionPeriod: `${y}-${m}` }));
                  }}
                  sx={{ flex: 1.2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0', borderWidth: 1.5 }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#f59e0b', borderWidth: 2 } } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ fontSize: 16, color: '#f59e0b' }} /></InputAdornment> }}>
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" value={form.collectionPeriod ? parseInt(form.collectionPeriod.split('-')[0]) || '' : ''}
                  onChange={e => {
                    const y = String(e.target.value);
                    const m = form.collectionPeriod ? form.collectionPeriod.split('-')[1] : '01';
                    setForm(f => ({ ...f, collectionPeriod: `${y}-${m}` }));
                  }}
                  sx={{ flex: 0.8, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0', borderWidth: 1.5 }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#f59e0b', borderWidth: 2 } } }}>
                  {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Assigned Contractors</Typography>
              {form.provider ? (
                (form.provider.assignedContractors || []).filter(a => a.isActive !== false).length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {(form.provider.assignedContractors || []).filter(a => a.isActive !== false).map(a => {
                      const c = a.contractor || {};
                      const cId = String(c.id || c._id || a.contractorId);
                      const isSelected = String(form.contractor) === cId;
                      return <Chip key={cId} label={fullName(c) || `Contractor #${cId}`} size="small"
                        onClick={() => handleContractorChange(cId)}
                        sx={{ fontWeight: 600, fontSize: '0.78rem', bgcolor: isSelected ? alpha('#8b5cf6', 0.25) : alpha('#8b5cf6', 0.1), color: '#7c3aed', borderRadius: 2, height: 28, cursor: 'pointer', border: isSelected ? '2px solid #7c3aed' : '2px solid transparent' }} />;
                    })}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>No contractors assigned</Typography>
                )
              ) : (
                <Typography sx={{ fontSize: '0.8rem', color: '#cbd5e1', mt: 0.5 }}>Select a provider first</Typography>
              )}
            </Grid>
          </Grid>

          {/* Services Table */}
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1.5 }}>Service Rows</Typography>
          <TableContainer sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 3, mb: 2, bgcolor: '#fafbfc' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ minWidth: 160, fontWeight: 700, fontSize: '0.75rem', borderRight: '2px solid', borderColor: 'divider', bgcolor: alpha('#f59e0b', 0.06) }}>Service</TableCell>
                  <TableCell colSpan={canViewBilling ? (canViewInvoice ? 4 : 3) : (canViewInvoice ? 2 : 1)} align="center" sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#0369a1', bgcolor: alpha('#0ea5e9', 0.08), borderRight: '2px solid', borderColor: 'divider', borderBottom: '1px solid', letterSpacing: '0.04em' }}>Provider</TableCell>
                  {(canViewCommission || canViewProfit) && (
                    <TableCell colSpan={(canViewCommission ? 2 : 0) + (canViewProfit ? 1 : 0)} align="center" sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#7e22ce', bgcolor: alpha('#a855f7', 0.08), borderBottom: '1px solid', letterSpacing: '0.04em' }}>Contractor &amp; Profit</TableCell>
                  )}
                  <TableCell rowSpan={2} sx={{ width: 50, bgcolor: alpha('#f59e0b', 0.06) }}></TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: alpha('#f59e0b', 0.04) }}>
                  <TableCell sx={{ minWidth: 110, fontWeight: 700, fontSize: '0.72rem', color: '#0369a1' }}>Collection $</TableCell>
                  {canViewBilling && <TableCell sx={{ minWidth: 80, fontWeight: 700, fontSize: '0.72rem', color: '#0369a1' }}>Type</TableCell>}
                  {canViewBilling && <TableCell sx={{ minWidth: 100, fontWeight: 700, fontSize: '0.72rem', color: '#0369a1' }}>Billing Rate</TableCell>}
                  {canViewInvoice && <TableCell sx={{ minWidth: 100, fontWeight: 700, fontSize: '0.72rem', color: '#0369a1', borderRight: '2px solid', borderColor: 'divider' }}>Invoice Amount</TableCell>}
                  {canViewCommission && <TableCell sx={{ minWidth: 100, fontWeight: 700, fontSize: '0.72rem', color: '#7e22ce' }}>Commission %</TableCell>}
                  {canViewCommission && <TableCell sx={{ minWidth: 100, fontWeight: 700, fontSize: '0.72rem', color: '#7e22ce' }}>Commission $</TableCell>}
                  {canViewProfit && <TableCell sx={{ minWidth: 100, fontWeight: 700, fontSize: '0.72rem', color: '#059669' }}>Agency Profit</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {form.services.map((svc, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: alpha('#f59e0b', 0.02) } }}>
                    <TableCell sx={{ py: 0.8 }}>
                      {svc.serviceName && svc.service ? (
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>{svc.serviceName}</Typography>
                      ) : (
                        <TextField select size="small" fullWidth value={svc.service || ''}
                          onChange={e => handleServiceSelect(idx, e.target.value)}
                          displayEmpty
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff' }, '& .MuiSelect-select': { fontSize: '0.82rem', py: 0.7 }, minWidth: 140 }}>
                          <MenuItem value="" disabled><em>Select service...</em></MenuItem>
                          {availableServices.filter(s => s._id && s.name).map(s => (
                            <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.8 }}>
                      <TextField size="small" variant="outlined" type="number" value={svc.collectionAmount}
                        onChange={e => updateServiceRow(idx, 'collectionAmount', e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>$</Typography></InputAdornment> }}
                        sx={{ '& input': { fontSize: '0.82rem', py: 0.6, px: 0.5 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, width: 110 }} />
                    </TableCell>
                    {canViewBilling && (
                      <TableCell sx={{ py: 0.8 }}>
                        <ToggleButtonGroup size="small" exclusive value={svc.isFixed ? 'fixed' : 'pct'}
                          onChange={(_, v) => { if (v) updateServiceRow(idx, 'isFixed', v === 'fixed'); }}
                          sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.3, fontSize: '0.65rem', fontWeight: 700, textTransform: 'none' } }}>
                          <ToggleButton value="pct">%</ToggleButton>
                          <ToggleButton value="fixed">$</ToggleButton>
                        </ToggleButtonGroup>
                      </TableCell>
                    )}
                    {canViewBilling && (
                      <TableCell sx={{ py: 0.8 }}>
                        <TextField size="small" variant="outlined" type="number" value={svc.billingRate}
                          onChange={e => updateServiceRow(idx, 'billingRate', e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>{svc.isFixed ? '$' : '%'}</Typography></InputAdornment> }}
                          sx={{ '& input': { fontSize: '0.82rem', py: 0.6, px: 0.5 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, width: 100 }} />
                      </TableCell>
                    )}
                    {canViewInvoice && (
                      <TableCell sx={{ py: 0.8, borderRight: (canViewCommission || canViewProfit) ? '2px solid' : 'none', borderColor: 'divider' }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#d97706' }}>{fmt(svc.billingAmount)}</Typography>
                      </TableCell>
                    )}
                    {canViewCommission && (
                      <TableCell sx={{ py: 0.8 }}>
                        <TextField size="small" variant="outlined" type="number" value={svc.commissionRate}
                          onChange={e => updateServiceRow(idx, 'commissionRate', e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>%</Typography></InputAdornment> }}
                          sx={{ '& input': { fontSize: '0.82rem', py: 0.6, px: 0.5 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, width: 100 }} />
                      </TableCell>
                    )}
                    {canViewCommission && (
                      <TableCell sx={{ py: 0.8 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#8b5cf6' }}>{fmt(svc.commission)}</Typography>
                      </TableCell>
                    )}
                    {canViewProfit && (
                      <TableCell sx={{ py: 0.8 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: svc.profit >= 0 ? '#059669' : '#dc2626' }}>{fmt(svc.profit)}</Typography>
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 0.8 }}>
                      {form.services.length > 1 && (
                        <IconButton size="small" onClick={() => removeServiceRow(idx)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Button size="small" startIcon={<Add />} onClick={addServiceRow} sx={{ color: '#3b82f6', fontWeight: 600 }}>Add Row</Button>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {(() => { const t = totals(form.services); return (
                <>
                  <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: alpha('#0ea5e9', 0.08), border: '1px solid', borderColor: alpha('#0ea5e9', 0.2) }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Provider</Typography>
                    <Typography sx={{ fontSize: '0.78rem' }}>Collected: <b style={{ color: '#3b82f6' }}>{fmt(t.collected)}</b>
                      {canViewInvoice && <> &nbsp;|&nbsp; Invoice: <b style={{ color: '#d97706' }}>{fmt(t.billed)}</b></>}
                    </Typography>
                  </Box>
                  {(canViewCommission || canViewProfit) && (
                    <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: alpha('#a855f7', 0.08), border: '1px solid', borderColor: alpha('#a855f7', 0.2) }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#7e22ce', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Contractor &amp; Profit</Typography>
                      <Typography sx={{ fontSize: '0.78rem' }}>
                        {canViewCommission && <>Commission: <b style={{ color: '#8b5cf6' }}>{fmt(t.commission)}</b></>}
                        {canViewCommission && canViewProfit && <> &nbsp;|&nbsp; </>}
                        {canViewProfit && <>Agency Profit: <b style={{ color: t.profit >= 0 ? '#059669' : '#dc2626' }}>{fmt(t.profit)}</b></>}
                      </Typography>
                    </Box>
                  )}
                </>
              ); })()}
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={() => handleSave(true)}
            disabled={saveAndGenerateLoading}
            startIcon={saveAndGenerateLoading ? <CircularProgress size={16} color="inherit" /> : <Receipt />}
            sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700, px: 3, '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' } }}>
            {saveAndGenerateLoading ? 'Generating...' : (editing ? 'Update & Generate Invoice' : 'Save & Generate Invoice')}
          </Button>
          <Button variant="contained" onClick={() => handleSave(false)}
            sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', fontWeight: 700, px: 4 }}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700 }}>Collection Details — {viewData?.collectionId}</Typography>
          <IconButton onClick={() => setViewOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {viewData && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {canViewProvider && (
                  <Grid item xs={4}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Provider</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{fullName(viewData.provider) || 'N/A'}</Typography>
                  </Grid>
                )}
                {canViewContractor && (
                  <Grid item xs={4}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Contractor</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{fullName(viewData.contractor) || '—'}</Typography>
                  </Grid>
                )}
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Period</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{viewData.collectionPeriod ? new Date(viewData.collectionPeriod).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '—'}</Typography>
                </Grid>
              </Grid>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, borderRight: '2px solid', borderColor: 'divider' }}>Service</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#0369a1', bgcolor: alpha('#0ea5e9', 0.06), borderRight: '2px solid', borderColor: 'divider', borderBottom: '1px solid' }}>Provider</TableCell>
                      {canViewInvoice && <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#0369a1', bgcolor: alpha('#0ea5e9', 0.06), borderRight: '2px solid', borderColor: 'divider', borderBottom: '1px solid' }}>Provider</TableCell>}
                      {canViewCommission && <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#7e22ce', bgcolor: alpha('#a855f7', 0.06), borderRight: '2px solid', borderColor: 'divider', borderBottom: '1px solid' }}>Contractor</TableCell>}
                      {canViewProfit && <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#059669', bgcolor: alpha('#10b981', 0.06), borderBottom: '1px solid' }}>Agency</TableCell>}
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#0369a1', borderRight: '2px solid', borderColor: 'divider' }}>Collected</TableCell>
                      {canViewInvoice && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#0369a1', borderRight: '2px solid', borderColor: 'divider' }}>Invoice Amount</TableCell>}
                      {canViewCommission && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#7e22ce', borderRight: '2px solid', borderColor: 'divider' }}>Commission</TableCell>}
                      {canViewProfit && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#059669' }}>Profit</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(viewData.services || []).map((s, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ borderRight: '2px solid', borderColor: 'divider' }}>{s.service?.name || s.serviceName || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, borderRight: '2px solid', borderColor: 'divider' }}>{fmt(s.collectionAmount)}</TableCell>
                        {canViewInvoice && <TableCell align="right" sx={{ borderRight: '2px solid', borderColor: 'divider' }}>{fmt(s.billingAmount)}</TableCell>}
                        {canViewCommission && <TableCell align="right" sx={{ borderRight: '2px solid', borderColor: 'divider' }}>{fmt(s.commission)}</TableCell>}
                        {canViewProfit && <TableCell align="right" sx={{ fontWeight: 700, color: s.profit >= 0 ? '#059669' : '#dc2626' }}>{fmt(s.profit)}</TableCell>}
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, borderRight: '2px solid', borderColor: 'divider' }}>Totals</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderRight: '2px solid', borderColor: 'divider' }}>{fmt(viewData.totalCollectionAmount)}</TableCell>
                      {canViewInvoice && <TableCell align="right" sx={{ fontWeight: 700, borderRight: '2px solid', borderColor: 'divider' }}>{fmt(viewData.totalBillingAmount)}</TableCell>}
                      {canViewCommission && <TableCell align="right" sx={{ fontWeight: 700, borderRight: '2px solid', borderColor: 'divider' }}>{fmt(viewData.totalCommission)}</TableCell>}
                      {canViewProfit && <TableCell align="right" sx={{ fontWeight: 700, color: viewData.totalProfit >= 0 ? '#059669' : '#dc2626' }}>{fmt(viewData.totalProfit)}</TableCell>}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Collection?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography>
          {deleteId?.force && (
            <Typography sx={{ color: '#f97316', fontSize: '0.85rem', mt: 1 }}>
              This collection has linked invoices. They will be unlinked but not deleted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Collections;
